/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import isNil from 'lodash/isNil';

import ErrorName from '../../error/error-name';
import LoaderRoot from '../../loader/loader-root';
import OrientationEnum from '../../orientation/orientation-enum';
import promisifyTimeout from '../../promisify/promisify-timeout';
import template from './viewer-root.html';

// TODO #295 Přesunout loading mimo tuto třídu.
// - Ideálně do něčeho jako LoaderService,
//   která bude napojená na loader vygenerovaní v `index.html`.
//   HTML kód loading by měl zůstat zachován bez zbytečného odebírání z DOM.
//   Díky tomu by do budoucna nebylo nutné řešit čekání na `rootVisible`.
// - Zatím je použitý hotfix s přepínáním mezi dvěma elementy `viewer-root`.
//   Při použití samostatného `v-if` bez `v-else` se vkládá rušivý komentář.
//   Na pozadí stejně Vue `viewer-root` element recykluje,
//   takže se při přepnutí zachová.
// TODO Přidat UML diagram s posloupností spouštění prolínače (orientace, obsah, loading).
export default {
  name: 'ViewerRoot',
  template,
  components: { LoaderRoot },
  computed: {
    className() {
      const { orientationEnum } = this;
      switch (orientationEnum) {
        case OrientationEnum.CLOCKWISE_0:
          return 'viewer-root--orientation-0';
        case OrientationEnum.CLOCKWISE_90:
          return 'viewer-root--orientation-90';
        case OrientationEnum.CLOCKWISE_180:
          return 'viewer-root--orientation-180';
        case OrientationEnum.CLOCKWISE_270:
          return 'viewer-root--orientation-270';
        default:
          return '';
      }
    },
    loaderVisible() {
      // Musí být známa orientace,
      // aby se mohli určit rozměry do kterých se bude vykreslovat.
      return this.orientationEnum;
    },
    rootVisible() {
      // Dokud není doručen nějaký obsah,
      // tak se zobrazuje loading animace.
      return this.contentReady && this.orientationEnum;
    }
  },
  created() {
    /**
     * Ovladač zrušení aktuálně probíhajícího čekání na odeslání reportu.
     * Inicializuje se ovladač, který nic neovládá a s každou změnou obsahu
     * pak obsahuje ovladač spojení s poslední změnou obsahu.
     * @type {AbortController}
     */
    this.reportAbortController = new AbortController();
    /**
     * Zpoždění, po kterém se odešle report.
     * @type {number}
     */
    this.reportWaitDelay = 6 * 1000;
    // Naslouchání událostem.
    const { device } = this.$root;
    device.on('content', this.onContent);
    // Aktualizace orientace zařízení.
    this.refresh();
    // Spuštění synchronizace.
    device.connect().catch((error) => this.handleError(error));
  },
  data() {
    return {
      contentReady: !isNil(this.$root.session.contentTimestamp),
      orientationEnum: null
    };
  },
  destroyed() {
    const { device } = this.$root;
    // Zrušení naslouchání událostem.
    device.off('content', this.onContent);
    const { director } = this;
    if (!isNil(director)) {
      this.destroyDirector(director);
    }
    // Zrušení případného probíhajícího reportu.
    this.reportAbortController.abort();
  },
  methods: {
    createDirector() {
      const director = this.$root.device.createDirector(this.$el);
      director.on('error', this.onError);
      director.on('played', this.onPlayed);
      return director;
    },
    destroyDirector(director) {
      director.off('error', this.onError);
      director.off('played', this.onPlayed);
      this.$root.device.destroyDirector(director);
    },
    handleError(error) {
      this.$emit('error', error);
    },
    onContent() {
      this.contentReady = true;
      // Zrušení případného probíhajícího reportu.
      this.reportAbortController.abort();
      const newController = new AbortController();
      this.reportAbortController = newController;
      // Aktualizace orientace zařízení.
      // TODO Logičtější by asi bylo, aby reagovalo na změnu orientace a ne obsahu.
      this.refresh();
      // Odeslání reportu s HW informacemi
      // Pro odeslání reportu musí existovat inicializovaný přehrávač.
      this.report(newController.signal);
    },
    onError(error) {
      this.handleError(error);
    },
    onPlayed(scheduleInfo) {
      this.$root.device
        .hit(scheduleInfo)
        .catch((error) => this.handleError(error));
    },
    refresh() {
      this.$root.screenManager
        .getCssOrientation()
        .then((orientationEnum) => {
          this.orientationEnum = orientationEnum;
        })
        .catch((error) => this.handleError(error));
    },
    /**
     * Odešle HW report se zpožděním pro spolehlivější nasnímání obrazu.
     *
     * Propracované řešení by vyžadovalo rozsáhlejší úpravy v přehrávači.
     * Musela by se detekovat změna obsahu v jednotlivých vláknech přehrávače.
     * Konkrétně by se museli detekovat tyto stavy pro každé vlákno:
     *
     * - Při změně z jednoho obsahu na druhý, který není prázdný, se čeká
     *   na `entered` událost z nového obsahu.
     * - Při změně z neprázdného obsahu na prázdný se čeká na `left` událost
     *   z původního obsahu.
     * - Při změně prázdného obsahu na prázdný, lze pořídit screenshot ihned.
     *
     * Jakmile by všechny vlákna tento stav splnila, mohl by se report odeslat.
     *
     * Stejně by ale toto případné propracované řešení nebylo dokonalé:
     *
     * - U widgetů a webů není přesný okamžik načtení známý.
     *   Při zobrazení se teprve začínají načítat.
     * - Mnoho videí začíná černou obrazovkou, opět je vhodné pár vteřin počkat,
     *   než se začne zobrazovat skutečný obsah.
     * - webOS nedokáže pořídit screenshot více jak 1 videa viz #128,
     *   takže řešit u něj čekání na více videí bude kontraproduktivní.
     * @param {AbortSignal} abortSignal Signál pro předčasné přerušení.
     * @returns {Promise.<undefined>}
     */
    report(abortSignal) {
      return new Promise((resolve) => {
        const timeoutAbortController = new AbortController();
        if (abortSignal.aborted) {
          return resolve();
        }
        const abortListener = () => {
          timeoutAbortController.abort();
          abortSignal.removeEventListener('abort', abortListener);
        };
        abortSignal.addEventListener('abort', abortListener);
        return resolve(
          promisifyTimeout(this.reportWaitDelay, { abortSignal })
            // Získání informací do reportu a jeho odeslání na server.
            .then(() => this.$root.device.report())
            .catch((error) => {
              // Chyby spojené s předčasným přerušením reportu jsou ignorovány.
              if (error.name === ErrorName.AbortError) {
                return;
              }
              this.handleError(error);
            })
        );
      });
    }
  },
  watch: {
    // TODO #295 Hotfix spuštění prolínače.
    // - Technický dluh z předchozí implementace se zanořením do ViewerList.
    // - Spoléhá se, že již prolínač nebude deaktivován,
    //   že se nebude vytvářet opakovaně apod.
    // - Ideálně by se komponenta neměla zobrazovat,
    //   dokud nebudou přebné informace dostupné (orientace, obsah, ...).
    rootVisible(value) {
      if (!value || this.director) {
        // Dokud nejsou informace dostupné, nic se nedělá.
        // Opakovaná volání jsou také ignorována.
        return;
      }
      // Počká se na vykreslení elementu a zahájí se zobrazování prolínače.
      // Vue nedovoluje pokročilou správu elementů,
      // takže nelze pro implmentaci prolínače použít.
      this.$nextTick(() => {
        const director = this.createDirector();
        this.director = director;
        this.report(this.reportAbortController.signal);
      });
    }
  }
};
